import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@arcadeai/design-system/components/ui/atoms/popover";
import { Switch } from "@arcadeai/design-system/components/ui/atoms/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { Loader2, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { useDeleteUserSource, useUpdateUserSource } from "../../api/hooks";
import type { UserSource } from "../../types";
import { UserSourceSheet } from "../user-source-sheet";

export const UserSourceActions = ({
  userSource,
  gatewayCount = 0,
  gatewaysIsPending = false,
  gatewaysIsError = false,
}: {
  userSource: UserSource;
  gatewayCount?: number;
  gatewaysIsPending?: boolean;
  gatewaysIsError?: boolean;
}) => {
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { mutateAsync: remove, isPending: isDeleting } = useDeleteUserSource(
    userSource.id
  );
  const { mutate: update, isPending: isUpdating } = useUpdateUserSource(
    userSource.id
  );

  const isActive = userSource.status === "active";
  const deleteBlocked =
    !(gatewaysIsPending || gatewaysIsError) && gatewayCount > 0;
  const blockedMessage = `In use by ${gatewayCount} active gateway${gatewayCount === 1 ? "" : "s"}. Disable or remove the gateway${gatewayCount === 1 ? "" : "s"} first.`;

  const handleStatusChange = (checked: boolean) => {
    update({ status: checked ? "active" : "inactive" });
  };

  const handleDelete = async () => {
    try {
      await remove();
      setIsDeleteOpen(false);
    } catch {
      // error toast handled globally via meta.errorMessage
    }
  };

  return (
    <div className="flex items-center justify-start gap-1">
      <TooltipProvider delay={200}>
        <Tooltip>
          <TooltipTrigger
            render={
              <span>
                <Switch
                  aria-label={
                    isActive ? "Deactivate user source" : "Activate user source"
                  }
                  checked={isActive}
                  disabled={isUpdating || (isActive && deleteBlocked)}
                  onCheckedChange={handleStatusChange}
                />
              </span>
            }
          />
          <TooltipContent>
            {isActive && deleteBlocked ? (
              <p>{blockedMessage}</p>
            ) : (
              <p>{isActive ? "Deactivate" : "Activate"}</p>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <UserSourceSheet
        trigger={
          <Button aria-label="Edit user source" size="icon" variant="ghost">
            <Pencil className="size-4" />
          </Button>
        }
        userSource={userSource}
      />
      {deleteBlocked ? (
        <TooltipProvider delay={200}>
          <Tooltip>
            <TooltipTrigger
              render={
                <span>
                  <Button
                    aria-label="Delete user source"
                    className="text-destructive/70"
                    disabled
                    size="icon"
                    variant="ghost"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </span>
              }
            />
            <TooltipContent>
              <p>{blockedMessage}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <Popover onOpenChange={setIsDeleteOpen} open={isDeleteOpen}>
          <PopoverTrigger
            render={
              <Button
                aria-label="Delete user source"
                className="text-destructive/70 hover:text-destructive"
                disabled={isDeleting}
                size="icon"
                variant="ghost"
              />
            }
          >
            {isDeleting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Trash2 className="size-4" />
            )}
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="grid gap-4">
              <div className="space-y-2">
                <h4 className="font-medium leading-none">Delete user source</h4>
                <p className="text-muted-foreground text-sm">
                  Are you sure you want to delete this user source? This action
                  cannot be undone.
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  onClick={() => setIsDeleteOpen(false)}
                  size="sm"
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  disabled={isDeleting}
                  onClick={handleDelete}
                  size="sm"
                  variant="destructive"
                >
                  {isDeleting ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    "Delete"
                  )}
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
